import React, { useState, useEffect, useRef, useMemo } from "react";
import { doc, getFirestore, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { isMobile, isTablet } from "react-device-detect";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Adsense } from "@ctrl/react-adsense";
import useSound from "use-sound";

import { createNullArray } from "utils/arrayUtils";
import { showAds } from "utils/ads";
import { setShowNavbar, setShowFriends } from "stores/uiState/UiStateSlice";
import { setGame } from "stores/game/GameSlice";
import {
  turns,
  soloTurns,
  latestPatchVersion,
  getChampionById,
  defaultStartingElo,
} from "constants/gameDefaults";
import { AdsenseAccount } from "constants/config";
import { printableNumber } from "utils/numbers";
import BlueTimer from "styles/images/views/game/blueSideTimer.png";
import RedTimer from "styles/images/views/game/redSideTimer.png";
import {
  isCompetitiveMode,
  GameModeEnum,
  GameStateEnum,
  GameStatusEnum,
} from "interfaces/game";
import Draft from "views/Game/states/Draft";
import RoleSelection from "views/Game/states/RoleSelection";
import Results from "views/Game/states/Results";
import ChampionEdit from "views/Game/states/ChampionEdit";
import WaitingGame from "components/WaitingGame";

// Import images
import toplaneIcon from "styles/images/roles/top.webp";
import jungleIcon from "styles/images/roles/jungle.webp";
import midIcon from "styles/images/roles/middle.webp";
import bottomIcon from "styles/images/roles/bottom.webp";
import supportIcon from "styles/images/roles/support.webp";
import noBanBlue from "styles/images/views/game/noBanBlue.png";
import noBanRed from "styles/images/views/game/noBanRed.png";
import leave from "styles/images/views/game/leave.svg";
import LeaveGamePopup from "components/popups/LeaveGamePopup";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { usePreview } from "react-dnd-preview";
import SoundControl from "components/sounds/SoundControl";

import notifBanSound from "styles/audio/notifban.ogg";
import notifPickSound from "styles/audio/notifpick.ogg";

const isMobileOrTablet = isMobile || isTablet;
const isTouchDevice = isMobileOrTablet;
const rolesIcon = [toplaneIcon, jungleIcon, midIcon, bottomIcon, supportIcon];

const TIMER_DRAFT_DURATION = 45;
const TIMER_SWAP_DURATION = 20;

const splashVariants = {
  hidden: { left: "-100%" },
  visible: { left: "0" },
};

const splashVariantsRedSide = {
  hidden: { left: "100%" },
  visible: { left: "0" },
};

// Hack a preview during drag for touch backends
function DraggablePreview() {
  const preview = usePreview();
  const { item, style } = preview;
  if (!preview.display || !item.id) {
    return null;
  }

  return (
    <div className="pickCard" style={{ flex: "center", ...style }}>
      <motion.div
        style={{
          height: "13%",
          width: "25%",
          opacity: "50%",
          backgroundImage: `url(${getChampionById(item.id)?.splash || ""})`,
          backgroundPosition: "50% 30%",
          backgroundSize: "100%",
        }}
      />
    </div>
  );
}

function DraggableItem({ id, index, children, isSideDraggable, pick, side }) {
  const [{ isDragging }, drag] = useDrag({
    type: "champion",
    item: { id, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: isSideDraggable,
  });

  return (
    <div
      ref={drag}
      className={`pickCard ${
        side === "red" ? "redSide" : "blueSide"
      } pickCard${index} ${pick ? "picked" : ""}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isSideDraggable ? "grab" : "auto",
      }}
    >
      {children}
    </div>
  );
}

function DropTarget({ onDrop, children }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "champion", // This should match the type specified in useDrag.
    drop: (item) => {
      if (item && item.id) {
        onDrop(item.id);
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    // Optionally, you can specify the canDrop() method to add logic on whether the item can be dropped here
  });

  // Optional: Customize the style or className based on isOver or canDrop states
  const getStyle = () => ({
    // Your base styles
    width: "100%",
    height: "100%",
    // Styles when the draggable item is over the drop target
    backgroundColor: isOver && canDrop ? "lightgreen" : "transparent", // Example style
    // Other styles...
  });

  return (
    <div ref={drop} style={getStyle()}>
      {children}
    </div>
  );
}
function GameView({ db }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isMuted = useSelector((state) => state.audioReducer.isMuted);
  const [playNotifBanSound] = useSound(notifBanSound, { volume: 0.5 });
  const [playNotifPickSound] = useSound(notifPickSound, { volume: 0.5 });

  const user = useSelector((state) => state.userReducer.user);
  const game = useSelector((state) => state.gameReducer.game, shallowEqual);
  const gameRef = useRef(game);
  useEffect(() => {
    gameRef.current = game;
  }, [game]);
  const lobby = useSelector((state) => state.lobbyReducer.lobby);

  const [showLeaveGamePopup, setShowLeaveGamePopup] = useState(false);

  // Champion edit mode (simulation only)
  const startInEditMode = location.state?.startInEditMode === true;
  const [editMode, setEditMode] = useState(startInEditMode);
  const [selectedForReplacement, setSelectedForReplacement] = useState(null);

  // If the game state has not been fully initialized yet, assume this is turn 0.
  const turn = game?.currentTurn || 0;
  // TODO - revamp array logic which is currently a bit error prone.
  const currentSide =
    game?.mode === "simulation" ? soloTurns[turn]?.side : turns[turn]?.side;
  const currentPlayer = game?.players[turns[turn]?.side];
  const turnType =
    game?.mode === "simulation" ? soloTurns[turn]?.type : turns[turn]?.type;
  const opponentId = game?.players.filter(
    (playerId) => playerId !== user?.uid,
  )[0];
  const isPlayerTurn = currentPlayer === user?.uid;

  const userIsNotInGame =
    game?.mode === "simulation"
      ? false
      : game?.players.length === 2 && !game?.players.includes(user?.uid);

  if (userIsNotInGame) {
    // eslint-disable-next-line no-alert
    alert(
      "There is a bug, you are not in the game, please leave the game and try again",
    );
  }

  const playerSide = game?.players.findIndex((player) => player === user?.uid);

  const [blueSideUserId, setBlueSideId] = useState(game?.players[0]);
  const [redSideUserId, setRedSideId] = useState(game?.players[1]);

  const [blueSideUsername, setBlueSideUsername] = useState("");
  const [blueSideElo, setBlueSideElo] = useState(null);
  const [blueSideChampions, setBlueSideChampions] = useState(
    game?.bluePickedChampions,
  );
  const isBlueSideDraggable =
    game?.state === GameStateEnum.ROLE_SELECTION &&
    (game?.mode === GameModeEnum.SIMULATION ? true : playerSide === 0);

  useEffect(() => {
    setBlueSideChampions(game?.bluePickedChampions);
  }, [game?.bluePickedChampions]);

  const [redSideChampions, setRedSideChampions] = useState(
    game?.redPickedChampions,
  );
  const isRedSideDraggable =
    game?.state === GameStateEnum.ROLE_SELECTION &&
    (game?.mode === GameModeEnum.SIMULATION ? true : playerSide === 1);

  const [redSideUsername, setRedSideUsername] = useState("");
  const [redSideElo, setRedSideElo] = useState(null);

  useEffect(() => {
    setRedSideChampions(game?.redPickedChampions);
  }, [game?.redPickedChampions]);

  useEffect(() => {
    const fetchUserData = async (playerIndex, setUsername, setElo) => {
      const dbTmp = getFirestore();
      const userDoc = doc(dbTmp, "users", game?.players[playerIndex]);
      const userSnapshot = await getDoc(userDoc);

      if (userSnapshot.exists()) {
        setUsername(userSnapshot.data().username);
        if (game?.mode === GameModeEnum.COMPETITIVE) {
          setElo(userSnapshot.data().currentElo || defaultStartingElo);
        } else if (isCompetitiveMode(game?.mode)) {
          const gameModeElo =
            userSnapshot.data()?.modes?.[game?.mode]?.currentElo;
          setElo(gameModeElo || defaultStartingElo);
        }
      } else {
        console.error("No user found for opponent");
      }
    };

    if (
      game?.mode !== GameModeEnum.SIMULATION &&
      game?.players?.[0] &&
      game?.players?.[1]
    ) {
      setBlueSideId(game?.players[0]);
      setRedSideId(game?.players[1]);
      fetchUserData(0, setBlueSideUsername, setBlueSideElo);
      fetchUserData(1, setRedSideUsername, setRedSideElo);
    }
  }, [game?.players]);

  // Timer Part
  const [displayTimer, setDisplayTimer] = useState(TIMER_DRAFT_DURATION);
  const timerValueRef = useRef(TIMER_DRAFT_DURATION);
  const timerRef = useRef(null);

  function startTimer() {
    clearInterval(timerRef.current);
    if (game?.rolesLockedIn.length === 0) {
      if (game?.state === GameStateEnum.LIVE) {
        timerValueRef.current = TIMER_DRAFT_DURATION;
        setDisplayTimer(TIMER_DRAFT_DURATION);
      } else {
        timerValueRef.current = TIMER_SWAP_DURATION;
        setDisplayTimer(TIMER_SWAP_DURATION);
      }
    }
    if (
      game?.state === GameStateEnum.LIVE ||
      game?.state === GameStateEnum.ROLE_SELECTION
    ) {
      timerRef.current = setInterval(() => {
        timerValueRef.current -= 1;
        if (game?.rolesLockedIn.includes(user.uid)) {
          setDisplayTimer(null);
        } else {
          setDisplayTimer(timerValueRef.current);
        }
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }

  useEffect(() => {
    if (game?.mode !== GameModeEnum.SIMULATION) {
      startTimer();
    }
  }, [
    game?.state,
    game?.bluePickedChampions,
    game?.blueBannedChampions,
    game?.redPickedChampions,
    game?.redBannedChampions,
  ]);

  const [isLockedIn, setIsLockedIn] = useState(false);

  useEffect(() => {
    const handleTimeout = async () => {
      if (game?.state === GameStateEnum.LIVE) {
        if (isPlayerTurn && timerValueRef.current <= -10) {
          await updateDoc(doc(db, "games", game?.id), {
            state: GameStateEnum.FINISHED,
            status: GameStatusEnum.MISSED_TIMER,
            loser: { userId: user?.uid, score: 0 },
            winner: { userId: opponentId, score: 100 },
          });
          await updateDoc(doc(db, "lobbies", lobby?.id), {
            state: GameStateEnum.FINISHED,
            status: GameStatusEnum.MISSED_TIMER,
            loser: { userId: user?.uid, score: 0 },
            winner: { userId: opponentId, score: 100 },
          });
        }
        if (!isPlayerTurn && timerValueRef.current <= -20) {
          await updateDoc(doc(db, "games", game?.id), {
            state: GameStateEnum.FINISHED,
            status: GameStatusEnum.MISSED_TIMER,
            winner: { userId: user?.uid, score: 100 },
            loser: { userId: opponentId, score: 0 },
          });
          await updateDoc(doc(db, "lobbies", lobby?.id), {
            state: GameStateEnum.FINISHED,
            status: GameStatusEnum.MISSED_TIMER,
            winner: { userId: user?.uid, score: 100 },
            loser: { userId: opponentId, score: 0 },
          });
        }
      }
      if (game?.state === GameStateEnum.ROLE_SELECTION) {
        if (isLockedIn && game?.rolesLockedIn.length === 2) {
          await updateDoc(doc(db, "games", game.id), {
            state: GameStateEnum.FINISHED,
            status: GameStatusEnum.RESULTS,
          });
        }
        // Add timeout check for opponentId lock in
        if (
          // user has locked in
          isLockedIn &&
          // opponent has not locked in
          game?.rolesLockedIn?.length !== 2 &&
          // wait 15 extra seconds for safety
          timerRef.current === -15
        ) {
          const winnerLoser = {
            winner: {
              userId: user?.uid,
              score: 100,
            },
            loser: {
              userId: opponentId,
              score: 0,
            },
          };
          await updateDoc(doc(db, "games", game?.id), {
            state: "finished",
            status: "missed timer",
            ...winnerLoser,
          });
          await updateDoc(doc(db, "lobbies", lobby?.id), {
            state: "finished",
            status: "results",
            ...winnerLoser,
          });
        }
      }
    };

    if (game?.mode !== GameModeEnum.SIMULATION) {
      handleTimeout();
    }
  }, [
    isPlayerTurn,
    displayTimer,
    opponentId,
    db,
    game?.id,
    lobby?.id,
    user?.uid,
  ]);

  //  Selection Part
  const handleDropDnd = (droppedIndex, droppedId, side) => {
    const champions = side === 0 ? blueSideChampions : redSideChampions;
    const draggedIndex = champions.findIndex(
      (champion) => champion.championId === droppedId,
    );

    // Return if the dragged item is not found or index is the same
    if (draggedIndex === -1 || droppedIndex === draggedIndex) return;

    // Clone the array to avoid mutating the state directly
    const newChampions = [...champions];

    // Swap the items
    const temp = newChampions[droppedIndex];
    // eslint-disable-next-line fp/no-mutation
    newChampions[droppedIndex] = newChampions[draggedIndex];
    // eslint-disable-next-line fp/no-mutation
    newChampions[draggedIndex] = temp;

    // Update the state with the new champions array
    if (side === 0) {
      setBlueSideChampions(newChampions);
    } else {
      setRedSideChampions(newChampions);
    }
  };

  const blurBlueRef = useRef(null);
  const blurRedRef = useRef(null);
  const increasingRef = useRef(true);
  const currentBlurRef = useRef(30);
  const animationFrameIdRef = useRef(null);

  useEffect(() => {
    dispatch(setShowNavbar(false));
    dispatch(setShowFriends(false));
  }, []);

  useEffect(() => {
    const blurIncrement = 0.6;

    const animateBlur = () => {
      if (
        game?.currentTurn < Object.keys(soloTurns).length &&
        game?.state === GameStateEnum.LIVE
      ) {
        const blurRef = currentSide === 0 ? blurBlueRef : blurRedRef;

        if (!blurRef.current) return;

        if (increasingRef.current) {
          currentBlurRef.current += blurIncrement;
        } else {
          currentBlurRef.current -= blurIncrement;
        }

        if (currentBlurRef.current >= 60) {
          increasingRef.current = false;
        } else if (currentBlurRef.current <= 30) {
          increasingRef.current = true;
        }

        blurRef.current.setAttribute(
          "stdDeviation",
          currentBlurRef.current.toString(),
        );
        animationFrameIdRef.current = requestAnimationFrame(animateBlur);
      }
    };

    animateBlur();

    return () => {
      cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, [game]);

  const isTimerUpperThreshold = useMemo(
    () => timerValueRef.current >= -5,
    [timerValueRef.current >= -5],
  );

  const renderedDraft = useMemo(() => {
    if (game?.state === GameStateEnum.LIVE) {
      return <Draft db={db} canDoAnAction={isTimerUpperThreshold} />;
    }
    return null;
  }, [game?.state, isTimerUpperThreshold]);

  const renderedRoleSelection = useMemo(() => {
    if (game?.state === GameStateEnum.ROLE_SELECTION) {
      return (
        <RoleSelection
          db={db}
          blueSideTeam={blueSideChampions}
          redSideTeam={redSideChampions}
          isTimerUpperThreshold={isTimerUpperThreshold}
          isLockedIn={isLockedIn}
          setIsLockedIn={setIsLockedIn}
        />
      );
    }
    return null;
  }, [
    game?.state,
    blueSideChampions,
    redSideChampions,
    isTimerUpperThreshold,
    isLockedIn,
  ]);

  // Champion edit mode handlers
  const handleEditChampions = () => {
    setEditMode(true);
    setSelectedForReplacement(null);
  };

  const handlePickPortraitClick = (side, index) => {
    if (!editMode || game?.mode !== GameModeEnum.SIMULATION) return;
    if (!isMuted) playNotifPickSound();
    setSelectedForReplacement({ side, index });
  };

  const handleReplaceChampion = (newChampionId) => {
    if (!selectedForReplacement) return;
    const { side, index } = selectedForReplacement;
    const arrayName =
      side === "blue" ? "bluePickedChampions" : "redPickedChampions";
    const userId = side === "blue" ? "player1" : "player2";
    const currentGame = gameRef.current;

    const newChampions = [...currentGame[arrayName]];
    // eslint-disable-next-line fp/no-mutation
    newChampions[index] = { championId: newChampionId, userId };

    dispatch(setGame({ ...currentGame, [arrayName]: newChampions }));
    setSelectedForReplacement(null);
  };

  const handleSeeResults = () => {
    const currentGame = gameRef.current;
    const roles = ["top", "jungle", "mid", "bottom", "support"];
    const createTeamRecord = (team) =>
      roles.reduce(
        (acc, role, idx) => ({ ...acc, [role]: team[idx]?.championId }),
        {},
      );

    // Rebuild teams from current picks and reset winner to trigger a new API call
    dispatch(
      setGame({
        ...currentGame,
        teams: [
          {
            team: createTeamRecord(currentGame.bluePickedChampions),
            userId: "player1",
          },
          {
            team: createTeamRecord(currentGame.redPickedChampions),
            userId: "player2",
          },
        ],
        state: GameStateEnum.FINISHED,
        status: GameStatusEnum.CONNECTED,
        winner: null,
        loser: null,
      }),
    );

    setEditMode(false);
    setSelectedForReplacement(null);
  };

  const renderedResults =
    game?.state === GameStateEnum.FINISHED && !editMode ? (
      <Results
        db={db}
        onEditChampions={
          game?.mode === GameModeEnum.SIMULATION
            ? handleEditChampions
            : undefined
        }
      />
    ) : null;

  const renderedChampionEdit =
    editMode && game?.state === GameStateEnum.FINISHED ? (
      <ChampionEdit
        selectedForReplacement={selectedForReplacement}
        onSelectChampion={handleReplaceChampion}
        onSeeResults={handleSeeResults}
      />
    ) : null;

  // Update Header Related informations
  const leaveGameVariants = {
    hidden: { y: "-100%", opacity: 0 },
    visible: { y: "0%", opacity: 1 },
    exit: { y: "-100%", opacity: 0 },
  };
  const [uiLeaveBlueGame, setUiLeaveBlueGame] = useState(false);
  const [uiLeaveRedGame, setUiLeaveRedGame] = useState(false);
  const [phaseText, setPhaseText] = useState("");
  const [phaseClass, setPhaseClass] = useState(null);
  const [blueTimerText, setBlueTimerText] = useState("");
  const [blueTimerClass, setBlueTimerClass] = useState(null);
  const [redTimerText, setRedTimerText] = useState("");
  const [redTimerClass, setRedTimerClass] = useState(null);

  useEffect(() => {
    if (editMode) {
      setPhaseText("PICK");
      setPhaseClass("pick-phase");
    } else {
      setPhaseText(
        (game?.state === GameStateEnum.LIVE &&
          (turnType === "pick" ? "PICK" : "BAN")) ||
          (game?.state === GameStateEnum.ROLE_SELECTION ? "SWAP" : "RESULTS"),
      );

      setPhaseClass(
        (game?.state === GameStateEnum.LIVE && `${turnType}-phase`) ||
          (game?.state === GameStateEnum.ROLE_SELECTION
            ? "swap-phase"
            : "results-phase"),
      );
    }

    const getTimerText = (side) => {
      const sideUserId = side === 0 ? blueSideUserId : redSideUserId;
      // Win or lose without missed timer we prompt the score
      if (game?.status === GameStatusEnum.MISSED_TIMER) {
        if (game?.winner?.userId === sideUserId) {
          return "WIN";
        }
        return "LOSE";
      }
      if (game?.winner?.userId === sideUserId) {
        return game?.winner?.score;
      }
      if (game?.loser?.userId === sideUserId) {
        return game?.loser?.score;
      }
      // If game is not finished and it's the side's turn or in role selection mode and player is on the side
      if (
        game?.state !== GameStateEnum.FINISHED &&
        currentSide === side &&
        game?.mode !== GameModeEnum.SIMULATION
      ) {
        return displayTimer < 0 ? 0 : displayTimer;
      }
      if (
        game?.mode !== GameModeEnum.SIMULATION &&
        game?.state === GameStateEnum.ROLE_SELECTION &&
        playerSide === side
      ) {
        return displayTimer < 0 ? 0 : displayTimer;
      }
      return "";
    };

    const getTimerClass = (side) => {
      const sideUserId = side === 0 ? blueSideUserId : redSideUserId;
      // If game is finished, return win or lose based on the side
      if (game?.state === GameStateEnum.FINISHED) {
        if (game?.winner?.userId === sideUserId) {
          return "win";
        }
        if (game?.loser?.userId === sideUserId) {
          return "lose";
        }
      }
      // If it's the side's turn or in role selection mode and player is on the side
      if (currentSide === side && game?.state === GameStateEnum.LIVE) {
        return "isPlaying";
      }
      if (
        game?.mode !== GameModeEnum.SIMULATION &&
        game?.state === GameStateEnum.ROLE_SELECTION &&
        playerSide === side
      ) {
        return "isPlaying";
      }
      return "";
    };

    setBlueTimerText(getTimerText(0));
    setBlueTimerClass(getTimerClass(0));
    setRedTimerText(getTimerText(1));
    setRedTimerClass(getTimerClass(1));
  }, [
    currentSide, // used for simulation mode
    game?.state,
    game?.loser,
    game?.winner,
    game?.mode,
    displayTimer,
    editMode,
  ]);

  const [eloGainLoss, setEloGain] = useState(0);

  useEffect(() => {
    if (game?.mode !== GameModeEnum.COMPETITIVE) {
      return;
    }
    const initialGameElo = playerSide === 0 ? blueSideElo : redSideElo;
    if (!!user?.currentElo && !!initialGameElo) {
      setEloGain((user?.currentElo || defaultStartingElo) - initialGameElo);
    }
  }, [user?.currentElo]);

  // Use a similar logic for temporary game modes such as "competitive_wc"
  useEffect(() => {
    if (!isCompetitiveMode(game?.mode)) {
      return;
    }
    const initialGameElo = playerSide === 0 ? blueSideElo : redSideElo;
    const gameMode = game?.mode;
    const gameModeElo = user?.modes?.[gameMode]?.currentElo;
    if (!!gameModeElo && !!initialGameElo) {
      setEloGain((gameModeElo || defaultStartingElo) - initialGameElo);
    }
  }, [user?.modes]);

  const leaveGame = async () => {
    if (game?.mode !== GameModeEnum.SIMULATION) {
      const winnerAndLoser = {
        loser: {
          userId: user?.uid,
          score: 100,
        },
        winner: {
          userId: opponentId,
          score: 0,
        },
      };

      await updateDoc(doc(db, "games", game.id), {
        state: GameStateEnum.FINISHED,
        status: GameStatusEnum.SURREND,
        ...winnerAndLoser,
      });
      await updateDoc(doc(db, "lobbies", lobby.id), {
        state: GameStateEnum.FINISHED,
        status: GameStatusEnum.SURREND,
        ...winnerAndLoser,
      });
    }
    dispatch(setGame(null));
  };

  useEffect(() => {
    if (game?.state === GameStateEnum.LIVE && !isMuted) {
      playNotifBanSound();
    }
  }, [game?.state, isMuted]);

  useEffect(() => {
    if (!game) {
      navigate("/");
    }
  }, [game, navigate]);

  if (!game) {
    return null;
  }

  return (
    <>
      <SoundControl />
      <AnimatePresence>
        {showLeaveGamePopup && (
          <LeaveGamePopup
            gameMode={game?.mode}
            setShowLeaveGamePopup={setShowLeaveGamePopup}
            leaveGame={leaveGame}
          />
        )}
      </AnimatePresence>
      <div className="Game">
        <div className="backgroundLayer" />
        {game.state !== "waiting" && (
          <>
            <section className="header">
              <div className="playerInfoContainer">
                <div className="playerInfoWrapper blueSide">
                  <div className="playerUsername">
                    {playerSide === 0 && <p>YOU</p>}
                    {blueSideUsername}
                  </div>
                  {isCompetitiveMode(game.mode) && (
                    <div className="playerElo">
                      {`ELO - ${blueSideElo}`}
                      {game.winner && !!eloGainLoss && (
                        <p className={`eloGain ${blueTimerClass}`}>
                          {printableNumber(
                            playerSide === 0 ? eloGainLoss : -eloGainLoss,
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={`timerContainer blueSide ${blueTimerClass}`}>
                <div
                  className={`timerWrapper blueSide ${blueTimerClass}`}
                  onMouseEnter={() =>
                    setUiLeaveBlueGame(
                      playerSide === 0 || game.mode === GameModeEnum.SIMULATION,
                    )
                  }
                  onMouseLeave={() => setUiLeaveBlueGame(false)}
                >
                  <AnimatePresence>
                    {uiLeaveBlueGame && (
                      <motion.div
                        className="leaveGame"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={leaveGameVariants}
                      >
                        <motion.img
                          src={leave}
                          alt="Leave icon"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowLeaveGamePopup(true)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {blueTimerText}
                  {game.status === GameStatusEnum.RESULTS && "%"}
                </div>
              </div>
              <div className="gameStateContainer">
                <div className="gameStateWrapper">
                  <div
                    className="gameTitle"
                    style={isMobileOrTablet ? { fontSize: "3rem" } : {}}
                  >
                    Drafter
                  </div>
                  <div className="gameMode">
                    {game.mode.replaceAll("_", " ")}{" "}
                    {isMobileOrTablet ? "" : "Game"}
                  </div>
                </div>
                <div className={`gameState ${phaseClass}`}>
                  {phaseText} {isMobileOrTablet ? "" : "PHASE"}
                </div>
              </div>
              <div className={`timerContainer redSide ${redTimerClass}`}>
                <div
                  className={`timerWrapper redSide ${redTimerClass}`}
                  onMouseEnter={() =>
                    setUiLeaveRedGame(
                      playerSide === 1 || game.mode === GameModeEnum.SIMULATION,
                    )
                  }
                  onMouseLeave={() => setUiLeaveRedGame(false)}
                >
                  <AnimatePresence>
                    {uiLeaveRedGame && (
                      <motion.div
                        className="leaveGame"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={leaveGameVariants}
                      >
                        <motion.img
                          src={leave}
                          alt="Leave icon"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowLeaveGamePopup(true)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {redTimerText}
                  {game.status === GameStatusEnum.RESULTS && "%"}
                </div>
              </div>
              <div className="playerInfoContainer">
                <div className="playerInfoWrapper redSide">
                  <div className="playerUsername">
                    {redSideUsername}
                    {playerSide === 1 && <p>YOU</p>}
                  </div>
                  {isCompetitiveMode(game.mode) && (
                    <div className="playerElo">
                      {`ELO - ${redSideElo}`}
                      {game.winner && !!eloGainLoss && (
                        <p className={`eloGain ${redTimerClass}`}>
                          {printableNumber(
                            playerSide === 1 ? eloGainLoss : -eloGainLoss,
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
            <DndProvider backend={isTouchDevice ? TouchBackend : HTML5Backend}>
              <div className="gameContainer">
                <div className="pickContainer blueSide">
                  {[
                    ...(blueSideChampions || []),
                    ...createNullArray(5 - (blueSideChampions || []).length),
                  ].map((pick, index) => (
                    <div
                      className={`pickWrapper blueSide ${
                        editMode &&
                        selectedForReplacement?.side === "blue" &&
                        selectedForReplacement?.index === index
                          ? "selectedForReplacement"
                          : ""
                      }`}
                      key={`champion-blue-${pick?.championId || index}`}
                      role={editMode && pick ? "button" : undefined}
                      tabIndex={editMode && pick ? 0 : undefined}
                      onClick={() =>
                        editMode &&
                        pick &&
                        handlePickPortraitClick("blue", index)
                      }
                      onKeyDown={(e) => {
                        if (
                          editMode &&
                          pick &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          handlePickPortraitClick("blue", index);
                        }
                      }}
                      style={editMode && pick ? { cursor: "pointer" } : {}}
                    >
                      <DraggableItem
                        key={pick ? pick.championId : index}
                        id={pick ? pick.championId : null}
                        index={index}
                        isSideDraggable={isBlueSideDraggable}
                        pick={pick}
                      >
                        <DropTarget
                          onDrop={(droppedId) =>
                            handleDropDnd(index, droppedId, 0)
                          }
                          index={index}
                          side={0}
                        >
                          <div
                            className="backgroundGradient"
                            style={{
                              backgroundImage: `linear-gradient(0deg, rgba(31, 28, 32, 0.70) 0%, rgba(31, 28, 32, 0.70) 100%), url(${BlueTimer})`,
                            }}
                          />
                          <motion.div
                            className="championSplash"
                            animate={pick ? "visible" : "hidden"}
                            variants={splashVariantsRedSide}
                            initial="hidden"
                            transition={{ duration: 0.5 }}
                            style={{
                              backgroundImage: `url(${
                                getChampionById(pick?.championId)?.splash || ""
                              })`,
                            }}
                          />
                        </DropTarget>
                      </DraggableItem>
                      <img
                        className={`pickCardRole pickCardRole${index} ${
                          game.state === GameStateEnum.LIVE ? "drafting" : ""
                        } ${
                          game.state === GameStateEnum.FINISHED && !editMode
                            ? "results"
                            : ""
                        } ${editMode ? "editing" : ""}`}
                        alt="roles icon"
                        src={rolesIcon[index]}
                      />
                    </div>
                  ))}
                </div>

                {renderedDraft}
                {renderedRoleSelection}
                {renderedResults}
                {renderedChampionEdit}

                <div className="pickContainer redSide">
                  {[
                    ...(redSideChampions || []),
                    ...createNullArray(5 - (redSideChampions || []).length),
                  ].map((pick, index) => (
                    <div
                      className={`pickWrapper redSide ${
                        editMode &&
                        selectedForReplacement?.side === "red" &&
                        selectedForReplacement?.index === index
                          ? "selectedForReplacement"
                          : ""
                      }`}
                      key={`champion-red-${pick?.championId || index}`}
                      role={editMode && pick ? "button" : undefined}
                      tabIndex={editMode && pick ? 0 : undefined}
                      onClick={() =>
                        editMode &&
                        pick &&
                        handlePickPortraitClick("red", index)
                      }
                      onKeyDown={(e) => {
                        if (
                          editMode &&
                          pick &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          handlePickPortraitClick("red", index);
                        }
                      }}
                      style={editMode && pick ? { cursor: "pointer" } : {}}
                    >
                      <DraggableItem
                        key={pick ? pick.championId : index}
                        id={pick ? pick.championId : null}
                        index={index}
                        isSideDraggable={isRedSideDraggable}
                        side="red"
                        pick={pick}
                      >
                        <DropTarget
                          onDrop={(droppedId) =>
                            handleDropDnd(index, droppedId, 1)
                          }
                          index={index}
                          side={0}
                        >
                          <div
                            className="backgroundGradient"
                            style={{
                              backgroundImage: `linear-gradient(0deg, rgba(31, 28, 32, 0.70) 0%, rgba(31, 28, 32, 0.70) 100%), url(${RedTimer})`,
                            }}
                          />
                          <motion.div
                            className="championSplash"
                            animate={pick ? "visible" : "hidden"}
                            variants={splashVariants}
                            initial="hidden"
                            transition={{ duration: 0.5 }}
                            style={{
                              backgroundImage: `url(${
                                getChampionById(pick?.championId)?.splash || ""
                              })`,
                            }}
                          />
                        </DropTarget>
                      </DraggableItem>
                      {isTouchDevice && <DraggablePreview />}
                      <img
                        className={`pickCardRole pickCardRole${index} ${
                          game.state === GameStateEnum.LIVE ? "drafting" : ""
                        } ${
                          game.state === GameStateEnum.FINISHED && !editMode
                            ? "results"
                            : ""
                        } ${editMode ? "editing" : ""}`}
                        alt="roles icon"
                        src={rolesIcon[index]}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </DndProvider>
            {game?.mode !== GameModeEnum.SIMULATION && (
              <section className="banContainer">
                <div className="banWrapper blueSide">
                  {[
                    ...game.blueBannedChampions,
                    ...createNullArray(5 - game.blueBannedChampions.length),
                  ].map((pick, index) => (
                    <div
                      key={`champion-blue-${pick?.championId ?? index}`}
                      className={`banCard ${pick?.championId ?? ""}`}
                      style={{
                        backgroundPosition: "center center",
                        backgroundImage: (() => {
                          if (pick) {
                            if (pick.championId === "NoBan") {
                              return `url(${noBanBlue})`;
                            }
                            return `url(${
                              getChampionById(pick.championId)?.icon
                            })`;
                          }
                          return `linear-gradient(0deg, rgba(31, 28, 32, 0.70) 0%, rgba(31, 28, 32, 0.70) 100%), url(${BlueTimer})`;
                        })(),
                      }}
                    />
                  ))}
                </div>
                <div className="banWrapper redSide">
                  {[
                    ...game.redBannedChampions,
                    ...createNullArray(5 - game.redBannedChampions.length),
                  ].map((pick, index) => (
                    <div
                      key={`champion-red-${pick?.championId ?? index}`}
                      className={`banCard ${pick?.championId ?? ""}`}
                      style={{
                        backgroundPosition: "center center",
                        backgroundImage: (() => {
                          if (pick) {
                            if (pick.championId === "NoBan") {
                              return `url(${noBanRed})`;
                            }
                            return `url(${
                              getChampionById(pick.championId)?.icon
                            })`;
                          }
                          return `linear-gradient(0deg, rgba(31, 28, 32, 0.70) 0%, rgba(31, 28, 32, 0.70) 100%), url(${RedTimer})`;
                        })(),
                      }}
                    />
                  ))}
                </div>
              </section>
            )}
            <div className="patchVersionContainer">
              PATCH {latestPatchVersion}
            </div>
            {showAds(user) ? (
              <Adsense
                className="advertisingContainer"
                client={AdsenseAccount}
                slot="5279432204"
                style={{
                  bottom: 0,
                  clipPath: "none",
                  backgroundImage: "none",
                  backgroundColor: "transparent",
                  display: "inline-block",
                  width: "300px",
                  height: isMobile ? "50px" : "100px",
                }}
                format=""
              />
            ) : (
              <a href="/" style={{ pointerEvents: "none" }}>
                <div className="advertisingContainer" />
              </a>
            )}
          </>
        )}{" "}
        {game.state === "waiting" && (
          <WaitingGame
            message={`You're currently waiting for a ${game.mode.replaceAll(
              "_",
              " ",
            )} game.`}
          />
        )}
      </div>
    </>
  );
}

export default GameView;
